/**
 * Gate R2 Phase D2 — Company Portal authority helpers.
 *
 * Canonical terminology:
 *   Company Representative accessing the Company Portal.
 *
 * Shell entry / path allowlisting only. Server RLS remains authoritative for
 * project/report/asset payloads. Never grants organization-wide admin,
 * security settings, or platform controls.
 */
import type { User } from '@/types';
import { hasScopedAccessLevel } from '@/types';
import { getActiveScopedRelationships } from './scopedRelationshipResolver';

/** Canonical product labels (UI / evidence). */
export const COMPANY_PORTAL_LABEL = 'Company Portal';
export const COMPANY_REPRESENTATIVE_LABEL = 'Company Representative';
export const COMPANY_DASHBOARD_LABEL = 'Company Dashboard';

/**
 * Surfaces a Company Representative may enter inside the Company Portal
 * for their represented company only.
 */
export type CompanyPortalSurface =
  | 'dashboard'
  | 'projects'
  | 'project'
  | 'tasks'
  | 'assets'
  | 'reports'
  | 'decisions'
  | 'participants'
  | 'activity';

export const COMPANY_PORTAL_ALLOWED_SURFACES: ReadonlySet<CompanyPortalSurface> = new Set([
  'dashboard',
  'projects',
  'project',
  'tasks',
  'assets',
  'reports',
  'decisions',
  'participants',
  'activity',
]);

/**
 * Path prefixes that must NEVER appear as Company Portal destinations.
 * Fail closed: unknown admin / security / platform surfaces are denied.
 */
export const COMPANY_PORTAL_DENIED_PATH_PREFIXES: readonly string[] = [
  '/platform',
  '/security',
  '/settings/roles',
  '/settings/billing',
  '/settings/branding',
  '/workspaces/', // org workspace admin shells (users/security/settings)
  '/users',
  '/analytics',
];

export type CompanyPortalEntry =
  | { kind: 'one'; companyId: string }
  | { kind: 'many'; companyIds: string[] }
  | { kind: 'none' };

export function isCompanyPortalSurfaceAllowed(surface: string): surface is CompanyPortalSurface {
  return COMPANY_PORTAL_ALLOWED_SURFACES.has(surface as CompanyPortalSurface);
}

/**
 * Whether a candidate path is forbidden for Company Portal navigation.
 * Used to fail closed before redirecting a Company Representative.
 */
export function isDeniedCompanyPortalPath(pathname: string): boolean {
  const path = (pathname || '').split('?')[0];
  if (!path) return true;
  // Explicit company-portal tree is never treated as denied by prefix list.
  if (/^\/companies\/[^/]+\/(dashboard|projects|tasks|assets|reports|decisions|participants|activity)(\/|$)/.test(path)) {
    return false;
  }
  return COMPANY_PORTAL_DENIED_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) return path === prefix.slice(0, -1) || path.startsWith(prefix);
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

/**
 * Company ids the user represents via active company_representative relationships.
 * Prefer resourceType=company resourceId; fall back to representedCompanyId.
 */
export function getRepresentedCompanyIds(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): string[] {
  const ids = new Set<string>();
  for (const relationship of getActiveScopedRelationships(user)) {
    if (relationship.type !== 'company_representative') continue;
    if (!hasScopedAccessLevel(relationship.accessLevel, 'view')) continue;
    if (relationship.resourceType === 'company' && relationship.resourceId) {
      ids.add(relationship.resourceId);
      continue;
    }
    if (relationship.representedCompanyId) {
      ids.add(relationship.representedCompanyId);
    }
  }
  return [...ids].sort();
}

/** Fail-closed: user may enter Company Portal for this company only if they represent it. */
export function canAccessCompanyPortalCompany(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  companyId: string | null | undefined,
): boolean {
  if (!companyId) return false;
  return getRepresentedCompanyIds(user).includes(companyId);
}

export function resolveCompanyPortalEntry(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): CompanyPortalEntry {
  const companyIds = getRepresentedCompanyIds(user);
  if (companyIds.length === 0) return { kind: 'none' };
  if (companyIds.length === 1) return { kind: 'one', companyId: companyIds[0] };
  return { kind: 'many', companyIds };
}

export function companyPortalHref(
  companyId: string,
  surface: CompanyPortalSurface,
  options?: { projectId?: string },
): string {
  if (!companyId || !isCompanyPortalSurfaceAllowed(surface)) {
    return '/unauthorized';
  }
  switch (surface) {
    case 'dashboard':
      return `/companies/${companyId}/dashboard`;
    case 'projects':
      return `/companies/${companyId}/projects`;
    case 'project':
      return options?.projectId
        ? `/companies/${companyId}/projects/${options.projectId}`
        : `/companies/${companyId}/projects`;
    case 'tasks':
      return `/companies/${companyId}/tasks`;
    case 'assets':
      return `/companies/${companyId}/assets`;
    case 'reports':
      return `/companies/${companyId}/reports`;
    case 'decisions':
      return `/companies/${companyId}/decisions`;
    case 'participants':
      return `/companies/${companyId}/participants`;
    case 'activity':
      return `/companies/${companyId}/activity`;
    default:
      return '/unauthorized';
  }
}

/**
 * Resolve company id for in-portal navigation (D4).
 * Prefer route companyId when the user represents it.
 * Without a route companyId, use a single represented company.
 * Fail closed (null) when the route company is not represented, or when
 * representation is ambiguous / empty — never invent a company scope.
 */
export function resolveCompanyPortalNavCompanyId(
  routeCompanyId: string | null | undefined,
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): string | null {
  if (routeCompanyId) {
    return canAccessCompanyPortalCompany(user, routeCompanyId) ? routeCompanyId : null;
  }
  const entry = resolveCompanyPortalEntry(user);
  return entry.kind === 'one' ? entry.companyId : null;
}

/** Canonical project deep-link inside Company Portal; fail closed without company scope. */
export function companyPortalProjectHref(
  companyId: string | null | undefined,
  projectId: string,
  search = '',
): string {
  if (!companyId || !projectId) return '/unauthorized';
  const base = companyPortalHref(companyId, 'project', { projectId });
  if (!search) return base;
  return `${base}?${search.replace(/^\?/, '')}`;
}

/** Canonical surface href; fail closed without company scope. */
export function companyPortalSurfaceHref(
  companyId: string | null | undefined,
  surface: CompanyPortalSurface,
  options?: { projectId?: string; search?: string },
): string {
  if (!companyId) return '/unauthorized';
  const base = companyPortalHref(companyId, surface, {
    projectId: options?.projectId,
  });
  if (base === '/unauthorized' || !options?.search) return base;
  return `${base}?${options.search.replace(/^\?/, '')}`;
}

/**
 * Map a legacy `/client/*` (or `/portal/client`) path onto a Company Portal href.
 * Returns null when the legacy path cannot be mapped (fail closed).
 */
export function mapLegacyClientPathToCompanyPortal(
  pathname: string,
  companyId: string,
  search = '',
): string | null {
  if (!companyId || isDeniedCompanyPortalPath(pathname)) return null;
  const path = (pathname || '').split('?')[0];
  const suffix = search || '';

  if (path === '/portal/client' || path === '/client' || path === '/client/dashboard') {
    return `${companyPortalHref(companyId, 'dashboard')}${suffix}`;
  }

  const projectDetail = /^\/client\/projects\/([^/]+)\/?$/.exec(path);
  if (projectDetail?.[1]) {
    return `${companyPortalHref(companyId, 'project', { projectId: projectDetail[1] })}${suffix}`;
  }

  if (path === '/client/projects') {
    return `${companyPortalHref(companyId, 'projects')}${suffix}`;
  }
  if (path === '/client/reports' || path.startsWith('/client/reports/')) {
    return `${companyPortalHref(companyId, 'reports')}${suffix}`;
  }
  if (path === '/client/tasks') {
    return `${companyPortalHref(companyId, 'tasks')}${suffix}`;
  }
  if (path === '/client/assets') {
    return `${companyPortalHref(companyId, 'assets')}${suffix}`;
  }
  if (path === '/client/decisions') {
    return `${companyPortalHref(companyId, 'decisions')}${suffix}`;
  }
  if (path === '/client/participants') {
    return `${companyPortalHref(companyId, 'participants')}${suffix}`;
  }
  if (path === '/client/activity') {
    return `${companyPortalHref(companyId, 'activity')}${suffix}`;
  }

  // Unknown legacy client path — do not invent a destination.
  return null;
}

/**
 * Parse `:companyId` and optional portal surface from a Company Portal pathname.
 */
export function parseCompanyPortalPath(pathname: string): {
  companyId: string | null;
  surface: CompanyPortalSurface | null;
  projectId: string | null;
} {
  const path = (pathname || '').split('?')[0];
  const match = /^\/companies\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?/.exec(path);
  if (!match) {
    return { companyId: null, surface: null, projectId: null };
  }
  const companyId = match[1] || null;
  const segment = match[2] || 'dashboard';
  if (segment === 'projects' && match[3]) {
    return { companyId, surface: 'project', projectId: match[3] };
  }
  if (isCompanyPortalSurfaceAllowed(segment)) {
    return { companyId, surface: segment, projectId: null };
  }
  return { companyId, surface: null, projectId: null };
}
